import React from "react";

import { useGetApplicationQuery, useGetPlanCostsQuery, useGetPlansQuery } from "EmployeeApplication/api";
import { WholeLifeContextProvider } from "EmployeeCoverage/Components/WholeLife/wholeLife.context";
import { WholeLifeEnrollmentModals } from "EmployeeCoverage/Components/WholeLife/wholeLife.modals";
import { WholeLifeMultiplePlans } from "EmployeeCoverage/Components/WholeLife/wholeLife.multiplePlans";
import { WholeLifePlan, PlanCost } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { WholeLifeSinglePlan } from "./wholeLife.singlePlan";

type Data =
  | {
      isLoading: true;
      plans: any;
      planCosts: any;
    }
  | {
      isLoading: false;
      plans: Array<WholeLifePlan>;
      planCosts: Array<PlanCost>;
    };

const useData = (): Data => {
  const { isLoading: isLoadingApplication, data: { employeePolicy: { id } = {} } = {} } = useGetApplicationQuery();
  const { isLoading: isLoadingPlans, data: { plans = [] } = {} } = useGetPlansQuery(
    {
      id,
      type: "whole_life",
    },
    { skip: !id },
  );
  const { isLoading: isLoadingCosts, data: { planCosts } = {} } = useGetPlanCostsQuery(
    {
      id,
      type: "whole_life",
    },
    { skip: plans.length === 0 },
  );

  return {
    isLoading: isLoadingApplication || isLoadingPlans || isLoadingCosts,
    plans,
    planCosts,
  } as Data;
};

/**
 * This component manages what to render as whole life plans and costs are loaded.
 */
const StateManager = () => {
  const { isLoading, plans = [], planCosts = [] } = useData();
  if (isLoading) {
    return <BasicLoading />;
  }
  if (plans.length === 1) {
    return <WholeLifeSinglePlan plan={plans[0]} planCost={planCosts.find(({ planID }) => planID === plans[0].id)} />;
  }
  if (plans.length > 1) {
    return <WholeLifeMultiplePlans plans={plans} planCosts={planCosts} />;
  }
  return null;
};

export const WholeLifePage = () => (
  <WholeLifeContextProvider>
    <StateManager />
    <WholeLifeEnrollmentModals />
  </WholeLifeContextProvider>
);
